import { Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { formatValueToSuffix, ProcessedBalance } from '@/utils/utils'

import TooltipTitle from '../TooltipTitle'

const MAX_WITHOUT_SUFFIX = 100000000
interface StakeCalculatorProps {
  amount: number
  newStake: number
  stakeMaxDays: number
  stakeMultiplier: number
  currentStaked: ProcessedBalance
  currentStake: ProcessedBalance
  rl1PerDay: number
  stakeSupply: ProcessedBalance
  state?: 'stake' | 'restake' | 'withdraw'
}

const StakeCalculator: React.FC<StakeCalculatorProps> = ({
  amount,
  newStake,
  stakeMaxDays,
  stakeMultiplier,
  currentStaked,
  currentStake,
  rl1PerDay,
  stakeSupply,
  state = 'stake',
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)

  // Current values
  const currStakedNumber = useMemo(() => parseFloat(currentStaked.fullValue), [currentStaked])
  const currStakeNumber = useMemo(() => parseFloat(currentStake.fullValue), [currentStake])
  const currStakeSupplyNumber = useMemo(() => parseFloat(stakeSupply.fullValue), [stakeSupply])
  const currRl1PerDayNumber = useMemo(() => rl1PerDay / 1e18, [rl1PerDay])
  const currRewards = useMemo(
    () =>
      currStakeSupplyNumber === 0
        ? 0
        : (currRl1PerDayNumber * currStakeNumber) / currStakeSupplyNumber,
    [currRl1PerDayNumber, currStakeNumber, currStakeSupplyNumber]
  )

  // New values
  const afterStaked = useMemo(() => currStakedNumber + amount, [currStakedNumber, amount])
  const afterStake = useMemo(() => currStakeNumber + newStake, [currStakeNumber, newStake])
  const afterStakeSupply = useMemo(
    () => currStakeSupplyNumber + newStake,
    [currStakeSupplyNumber, newStake]
  )
  const afterRL1 = useMemo(
    () => (afterStakeSupply === 0 ? 0 : (currRl1PerDayNumber * afterStake) / afterStakeSupply),
    [currRl1PerDayNumber, afterStake, afterStakeSupply]
  )
  const stakePercentage = useMemo(
    () => (afterStake / afterStakeSupply) * 100,
    [afterStake, afterStakeSupply]
  )

  // Added reward is weird because it's computed as after - curr
  const newRewards = useMemo(() => afterRL1 - currRewards, [afterRL1, currRewards])

  // Compute APY and rl1 per day per l1
  const rl1perDayPerL1 = useMemo(
    () => (afterStaked > 0 ? afterRL1 / afterStaked : 0),
    [afterRL1, afterStaked]
  )
  const rl1Apy = useMemo(
    () =>
      afterStakeSupply === 0
        ? 0
        : (currRl1PerDayNumber / afterStakeSupply) * 365 * stakeMaxDays * 100,
    [currRl1PerDayNumber, afterStakeSupply]
  )

  return (
    <Stack>
      <TooltipTitle
        title={t('components.staking.staking.modal.stake.calculator', 'Stake Calculator')}
        tooltip={t(
          'components.staking.staking.modal.stake.stakeCalculatorTooltip',
          `Use this calculator to estimate how rewards and staking power will be generated from the tokens you’re staking. STAKE tokens will be released immediately to your account after staking. Rewards will unlock over time.
  
          Remember: The more you stake, and the longer your staking period, the more rewards and staking power you’ll generate on the network.`
        )}
      />
      <Stack style={{ gap: '0px' }}>
        <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Text
            style={{
              color: theme.colors.paragraph[0],
              fontSize: '18px',
            }}
          >
            {t('components.staking.staking.modal.stake.yield', 'Est. L1 Yield')}
          </Text>
          <Title order={4} style={{ color: theme.colors.paragraph[0] }}>
            {`${rl1Apy.toFixed(2)}% APY`}
          </Title>
        </Stack>
        <Stack style={{ alignItems: 'end' }}>
          <Text style={{ color: theme.colors.textAndIcon[0], fontSize: 14, textAlign: 'end' }}>
            {t(
              'components.staking.staking.modal.stake.yieldDescription',
              'Annual rate of daily unlocked RL1 rewards per staked L1'
            )}
          </Text>
        </Stack>
      </Stack>
      <Stack style={{ gap: '0px' }}>
        <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Text
            style={{
              color: theme.colors.paragraph[0],
              fontSize: '18px',
            }}
          >
            {
              t(
                'components.staking.staking.modal.stake.multiplier',
                'Staking Power Multiplier'
              ) as string
            }
          </Text>
          <Title order={4} style={{ color: theme.colors.paragraph[0] }}>
            {stakeMultiplier}x
          </Title>
        </Stack>
        <Stack style={{ alignItems: 'end' }}>
          <Text style={{ color: theme.colors.textAndIcon[0], fontSize: 14, textAlign: 'end' }}>
            {
              t(
                'components.staking.staking.modal.stake.multiplierDescription',
                'Used to calculate Staking Power per L1 staked'
              ) as string
            }
          </Text>
        </Stack>
      </Stack>
      <Stack
        style={{
          flexDirection: 'row',
          gap: 0,
          alignItems: 'center',
          backgroundColor: theme.colors.profileOutline[0],
          textAlign: 'right',
          borderRadius: 12,
          width: isMobile ? '100%' : '400px',
        }}
      >
        <Stack
          style={{
            width: '50%',
            gap: 20,
            paddingRight: 25,
            paddingLeft: 15,
            backgroundColor: theme.colors.background3[0],
            borderRadius: '12px 0 0 12px',
            height: isMobile ? 285 : 240,
            alignItems: 'end',
          }}
        >
          <Title style={{ marginTop: 20, fontSize: 14, whiteSpace: 'nowrap' }}>
            {t('components.staking.staking.modal.stake.netChange', 'Net Change')}
          </Title>
          <Stack style={{ gap: '5px' }}>
            <Title order={4} style={{ color: theme.colors.paragraph[0], textAlign: 'right' }}>
              {`${newRewards > 0 ? '+' : ''}${newRewards > MAX_WITHOUT_SUFFIX ? formatValueToSuffix(newRewards) : newRewards.toFixed(2)}`}
            </Title>
            <Text
              style={{
                color: theme.colors.paragraph[0],
                fontSize: '14px',
                wordWrap: 'break-word',
                height: 45,
              }}
            >
              {t('components.staking.staking.modal.stake.dailyRewards', 'Daily Rewards (RL1)')}
            </Text>
          </Stack>
          <Stack style={{ gap: '5px' }}>
            <Title order={4} style={{ color: theme.colors.paragraph[0], textAlign: 'right' }}>
              {`${newStake > 0 ? '+' : ''}${newStake > MAX_WITHOUT_SUFFIX ? formatValueToSuffix(newStake) : newStake.toFixed(2)}`}
            </Title>
            <Stack style={{ gap: '0px', marginBottom: 20, alignItems: 'flex-end' }}>
              <Text
                style={{
                  color: theme.colors.paragraph[0],
                  fontSize: '14px',
                  wordWrap: 'break-word',
                }}
              >
                {t('components.staking.staking.modal.stake.stakingPower', 'Staking Power (STAKE)')}
              </Text>
              {state === 'stake' && (
                <Text
                  style={{
                    color: theme.colors.paragraph[0],
                    fontSize: '14px',
                    wordWrap: 'break-word',
                  }}
                >
                  {t('components.staking.staking.modal.stake.votingPower', 'Voting Power (VOTE)')}
                </Text>
              )}
            </Stack>
          </Stack>
        </Stack>
        <Stack
          style={{
            backgroundColor: theme.colors.profileOutline[0],

            alignItems: 'end',
            width: '50%',
            borderRadius: '0 12px 12px 0',
            gap: 20,
            paddingRight: 25,
            paddingLeft: 15,
            height: isMobile ? 285 : 240,
          }}
        >
          <Title style={{ marginTop: 20, fontSize: 14 }}>
            {state === 'restake'
              ? t('components.staking.staking.modal.stake.afterRestake', 'After Restake')
              : state === 'withdraw'
                ? t('components.staking.staking.modal.stake.afterWithdrawal', 'After withdrawal')
                : t('components.staking.staking.modal.stake.afterDeposit', 'After Deposit')}
          </Title>
          <Stack style={{ gap: '5px' }}>
            <Title order={4} style={{ color: theme.colors.paragraph[0], textAlign: 'right' }}>
              {afterRL1 > MAX_WITHOUT_SUFFIX ? formatValueToSuffix(afterRL1) : afterRL1.toFixed(2)}
            </Title>

            <Text
              style={{
                color: theme.colors.paragraph[0],
                fontSize: '14px',
                wordWrap: 'break-word',
                height: 45,
                textAlign: 'right',
              }}
            >
              {rl1perDayPerL1.toFixed(2)} RL1 per STAKE
            </Text>
          </Stack>
          <Stack style={{ gap: '5px' }}>
            <Title order={4} style={{ color: theme.colors.paragraph[0], textAlign: 'right' }}>
              {afterStake > MAX_WITHOUT_SUFFIX
                ? formatValueToSuffix(afterStake)
                : afterStake.toFixed(2)}
            </Title>

            <Text
              style={{
                color: theme.colors.paragraph[0],
                fontSize: '14px',
                wordWrap: 'break-word',
                marginBottom: 20,
                textAlign: 'right',
              }}
            >
              {stakePercentage.toFixed(7)}% of all STAKE
            </Text>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}
export default StakeCalculator
